import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { AppCookie, clearBearerToken, DEBUG_WALLET } from "@bridgesplit/react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useSelector } from "react-redux";

import { ABF_GROUP_COOKIE_PREFIX, AUTH0_COOKIES_PREFIX, USER_WALLET_COOKIE } from "../constants";

// Import at the top of the file

export type AuthSliceState = {
    apiVersionUpdateNeeded: boolean;
    wallet: string | null;
};

const initialState: AuthSliceState = {
    apiVersionUpdateNeeded: false,
    wallet: null
};
const authSlice = createSlice({
    name: "authSlice",
    initialState,
    reducers: {
        resetAuthState: () => {
            return {
                apiVersionUpdateNeeded: false,
                wallet: null
            };
        },
        updateApiVersionNeeded: (state, action: PayloadAction<boolean>) => {
            state.apiVersionUpdateNeeded = action.payload;
        },
        updateWallet: (state, action: PayloadAction<string | null>) => {
            state.wallet = action.payload;
        }
    }
});

export default authSlice.reducer;
export const { updateApiVersionNeeded, updateWallet } = authSlice.actions;

export const getSlice = (state: { authSlice: AuthSliceState }): AuthSliceState => state.authSlice;

/**
 * Set values in cookies and reducer
 * Cookie storage allows credentials to be used in authenticated RTK queries
 * Reducer storage allows credentials to be used to trigger hook re-renders
 */
export function useAuthStorage() {
    const state: AuthSliceState = useSelector(getSlice);

    function resetCookies() {
        clearBearerToken(AUTH0_COOKIES_PREFIX);
        clearBearerToken(ABF_GROUP_COOKIE_PREFIX);
        AppCookie.remove(USER_WALLET_COOKIE);
    }

    return {
        state,
        resetCookies
    };
}

export function useAuth() {
    const { state } = useAuthStorage();
    const { connecting, connected } = useWallet();

    const isWalletBased = !!state.wallet || DEBUG_WALLET;
    const isAuthenticated = isWalletBased;

    const isWalletUnsyncedWithStore = connected && !state.wallet;
    const isLoading = connecting || isWalletUnsyncedWithStore;
    // There is a race condition where the wallet is connected but the store is not updated yet
    // This causes a flash of the loading state
    // We should find a better solution in the future

    return { isAuthenticated, isWalletBased, isLoading };
}
